import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { currentDuAnIdFromProfile } from '@/components/common/searchCommon/chiNhanhOptions'

export type HoaDonTabType = 'new' | 'edit' | 'clone' | 'from-invoice' | 'from-dat-hang'

/**
 * Loại chứng từ trong POS Bán hàng chung.
 * - `hoaDon`       : tab tạo/sửa hóa đơn (mặc định, backward-compat với state cũ).
 * - `traHang`      : tab tạo/sửa phiếu trả hàng.
 * - `datHang`      : tab tạo/sửa đặt hàng (chưa chốt thành hóa đơn).
 * - `yeuCauSuaChua`: tab tạo/sửa yêu cầu sửa chữa (POS 3 cột theo YCSCKiot).
 */
export type VoucherType = 'hoaDon' | 'traHang' | 'datHang' | 'yeuCauSuaChua'

export interface HoaDonTabFormData {
  maHoaDon: string
  thoiGian: string
  /**
   * User đã chủ động chọn lại «Thời gian bán».
   * false/undefined trên nháp tạo mới → khi mở lại / submit dùng giờ hiện tại.
   */
  thoiGianChonLai?: boolean
  /**
   * User đã chủ động chọn lại «Hạn giao hàng».
   * false/undefined trên nháp tạo mới → hạn giao = thời gian bán + 3 giờ.
   */
  thoiGianGiaoChonLai?: boolean
  activeTab: string
  khachHangId: string
  kenhBan: string
  bangGiaId?: string
  diaChiLayHangId?: string
  nguoiBanId?: string
  ghiChu: string
  giamGia: number
  giamGiaPhanTram: number
  giamGiaLoai?: 'VND' | 'PHAN_TRAM'
  lines: unknown[]
  giaoHang: Record<string, unknown>
  thuKhac: unknown[]
  phuongThucThanhToan?: string
  khachThanhToan?: number
  // Trả hàng-only fields (optional để share type với tab Hóa đơn)
  maPhieuTra?: string
  phiTraHang?: number
  hoaDonNguonId?: string
  maHoaDonNguon?: string
  maYeuCauSuaChua?: string
  /** Hàng thay thế / đổi (FE snapshot; BE chưa persist). */
  linesDoi?: unknown[]
}

export interface HoaDonTabSession {
  tabId: string
  type: HoaDonTabType
  /** Phân biệt tab Hóa đơn vs tab Trả hàng trong POS chung. */
  voucherType: VoucherType
  sourceHoaDonId?: string
  /**
   * ID đặt hàng gốc khi mở tab Hóa đơn mới từ Đặt hàng (type='from-dat-hang').
   * Dùng để detect trùng lặp khi user click lại nút "Xuất HĐ mới".
   */
  sourceDatHangId?: string
  title: string
  dirty: boolean
  formData?: HoaDonTabFormData
  savedHoaDonId?: string
  createdAt: number
}

export interface HoaDonPosState {
  /** Mảng (dự án) mà các tab nháp đang thuộc về — lưu localStorage theo key này. */
  duAnId: number | null
  tabs: HoaDonTabSession[]
  activeTabId: string | null
  counter: number
  traHangCounter: number
  datHangCounter: number
  yeuCauSuaChuaCounter: number
}

/** Bản cũ dùng chung mọi mảng — migrate 1 lần sang mảng đang đứng. */
const LEGACY_STORAGE_KEY = 'banhang.hoaDonPos.v1'
const LEGACY_MIGRATED_FLAG = 'banhang.hoaDonPos.v1.migratedToV2'

const storageKeyForDuAn = (duAnId: number) => `banhang.hoaDonPos.v2.duAn.${duAnId}`

const emptyState = (duAnId: number | null = null): HoaDonPosState => ({
  duAnId,
  tabs: [],
  activeTabId: null,
  counter: 0,
  traHangCounter: 0,
  datHangCounter: 0,
  yeuCauSuaChuaCounter: 0,
})

/** Dòng đã chọn hàng — form HĐ/ĐH/TH luôn có sẵn 1 dòng trống (`emptyProductLine`). */
const isFilledLine = (row: unknown): boolean => {
  if (!row || typeof row !== 'object') return false
  const r = row as Record<string, unknown>
  return (
    String(r.hangHoaId ?? '').trim() !== '' ||
    r.product != null ||
    String(r.tenHang ?? '').trim() !== ''
  )
}

const hasFilledLines = (v: unknown) => Array.isArray(v) && v.some(isFilledLine)

/**
 * Tab đã có dòng hàng (HĐ/ĐH/TH: `lines`/`linesDoi`; YCSC: `hangHoaList`).
 * Field khác (khách, địa chỉ giao…) form hay tự điền sẵn → không dùng để coi là "đã nhập".
 */
export const hoaDonTabHasLines = (tab: HoaDonTabSession): boolean => {
  if (!tab.formData) return false
  const f = tab.formData as unknown as Record<string, unknown>
  return hasFilledLines(f.lines) || hasFilledLines(f.linesDoi) || hasFilledLines(f.hangHoaList)
}

/** Tab tạo mới chưa có dòng hàng — không lưu nháp, đóng không cần xác nhận. */
export const isHoaDonTabEmpty = (tab: HoaDonTabSession): boolean =>
  tab.type === 'new' && !hoaDonTabHasLines(tab)

/** Đóng tab có cần hỏi lại không: chỉ khi đã sửa và còn dòng hàng. */
export const hoaDonTabNeedsCloseConfirm = (tab: HoaDonTabSession): boolean =>
  tab.dirty && hoaDonTabHasLines(tab)

/** Số cuối title tab mới (`pos.tabTitle` mọi locale đều là "… {n}"). */
const tabTitleSeq = (title: string): number => {
  const m = /(\d+)\s*$/.exec(title)
  return m ? Number(m[1]) : 0
}

/**
 * Số cho tab tạo mới: lớn nhất trong các tab mới cùng loại đang mở + 1.
 * Không dùng counter tích lũy — tab đã đóng / tab trống bị bỏ không làm số nhảy.
 */
export const nextNewTabSeq = (tabs: HoaDonTabSession[], voucherType: VoucherType): number =>
  tabs.reduce(
    (max, t) =>
      t.type === 'new' && t.voucherType === voucherType ? Math.max(max, tabTitleSeq(t.title)) : max,
    0,
  ) + 1

/** Bỏ tab trống; counter loại nào không còn tab thì về 0 (tab kế tiếp lại là "… 1"). */
const withoutEmptyTabs = (state: HoaDonPosState): HoaDonPosState => {
  const tabs = state.tabs.filter((t) => !isHoaDonTabEmpty(t))
  const keepCounter = (voucherType: VoucherType, n: number) =>
    tabs.some((t) => t.voucherType === voucherType) ? n : 0
  return {
    duAnId: state.duAnId,
    tabs,
    activeTabId: tabs.some((t) => t.tabId === state.activeTabId)
      ? state.activeTabId
      : tabs[0]?.tabId ?? null,
    counter: keepCounter('hoaDon', state.counter),
    traHangCounter: keepCounter('traHang', state.traHangCounter),
    datHangCounter: keepCounter('datHang', state.datHangCounter),
    yeuCauSuaChuaCounter: keepCounter('yeuCauSuaChua', state.yeuCauSuaChuaCounter),
  }
}

const normalizeTabs = (rawTabs: HoaDonTabSession[]): HoaDonTabSession[] =>
  rawTabs.filter(Boolean).map((t) => {
    const raw = t as HoaDonTabSession & {
      voucherType?: string
      type?: string
      sourceDonHangId?: string
    }
    let voucherType = (raw.voucherType || 'hoaDon') as VoucherType
    if ((voucherType as string) === 'donHang') voucherType = 'datHang'
    let type = (raw.type || 'new') as HoaDonTabType
    if ((type as string) === 'from-don-hang') type = 'from-dat-hang'
    const sourceDatHangId = raw.sourceDatHangId ?? raw.sourceDonHangId
    return { ...raw, voucherType, type, sourceDatHangId }
  })

const parseStoredState = (raw: string, duAnId: number): HoaDonPosState | null => {
  try {
    const parsed = JSON.parse(raw) as Partial<HoaDonPosState> & { tabs?: HoaDonTabSession[] }
    if (!Array.isArray(parsed.tabs)) return null
    const tabs = normalizeTabs(parsed.tabs)
    const parsedAny = parsed as Partial<HoaDonPosState> & { donHangCounter?: number }
    return withoutEmptyTabs({
      duAnId,
      tabs,
      activeTabId: parsed.activeTabId ?? null,
      counter: parsed.counter ?? tabs.length,
      traHangCounter: parsed.traHangCounter ?? 0,
      datHangCounter: parsed.datHangCounter ?? parsedAny.donHangCounter ?? 0,
      yeuCauSuaChuaCounter: parsed.yeuCauSuaChuaCounter ?? 0,
    })
  } catch {
    return null
  }
}

/** Migrate key chung v1 → mảng hiện tại (một lần). */
const migrateLegacyIfNeeded = (duAnId: number): string | null => {
  if (typeof window === 'undefined') return null
  try {
    if (window.localStorage.getItem(LEGACY_MIGRATED_FLAG)) return null
    const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY)
    if (!legacy) {
      window.localStorage.setItem(LEGACY_MIGRATED_FLAG, '1')
      return null
    }
    const key = storageKeyForDuAn(duAnId)
    if (!window.localStorage.getItem(key)) {
      window.localStorage.setItem(key, legacy)
    }
    window.localStorage.removeItem(LEGACY_STORAGE_KEY)
    window.localStorage.setItem(LEGACY_MIGRATED_FLAG, '1')
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

export const loadHoaDonPosStateForDuAn = (duAnId: number | null): HoaDonPosState => {
  if (typeof window === 'undefined' || duAnId == null || !Number.isFinite(duAnId) || duAnId <= 0) {
    return emptyState(duAnId)
  }
  try {
    const key = storageKeyForDuAn(duAnId)
    let raw = window.localStorage.getItem(key)
    if (!raw) {
      raw = migrateLegacyIfNeeded(duAnId)
    }
    if (!raw) return emptyState(duAnId)
    return parseStoredState(raw, duAnId) ?? emptyState(duAnId)
  } catch {
    return emptyState(duAnId)
  }
}

const loadInitial = (): HoaDonPosState => {
  const duAnId = currentDuAnIdFromProfile()
  return loadHoaDonPosStateForDuAn(duAnId)
}

const persist = (state: HoaDonPosState) => {
  if (typeof window === 'undefined') return
  const duAnId =
    state.duAnId != null && Number.isFinite(state.duAnId) && state.duAnId > 0
      ? state.duAnId
      : currentDuAnIdFromProfile()
  if (duAnId == null) return
  try {
    // Tab trống vẫn nằm trong store (đang mở trên UI) nhưng không lưu nháp.
    const toSave = withoutEmptyTabs({ ...state, duAnId })
    window.localStorage.setItem(storageKeyForDuAn(duAnId), JSON.stringify(toSave))
  } catch {
    // ignore quota / serialize errors
  }
}

const applyLoaded = (state: HoaDonPosState, loaded: HoaDonPosState) => {
  state.duAnId = loaded.duAnId
  state.tabs = loaded.tabs
  state.activeTabId = loaded.activeTabId
  state.counter = loaded.counter
  state.traHangCounter = loaded.traHangCounter
  state.datHangCounter = loaded.datHangCounter
  state.yeuCauSuaChuaCounter = loaded.yeuCauSuaChuaCounter
}

const initialState: HoaDonPosState = loadInitial()

const hoaDonPosDucks = createSlice({
  name: 'hoaDonPos',
  initialState,
  reducers: {
    /**
     * Đổi mảng (dự án): lưu nháp mảng cũ (đã persist theo key cũ), nạp nháp mảng mới.
     * Cùng mảng → no-op.
     */
    hydrateHoaDonPosForDuAn(state, action: PayloadAction<number | null>) {
      const nextDuAnId = action.payload
      const next =
        nextDuAnId != null && Number.isFinite(nextDuAnId) && nextDuAnId > 0
          ? nextDuAnId
          : null
      if (state.duAnId === next) return
      // Persist mảng hiện tại trước khi đổi (phòng case chưa flush).
      if (state.duAnId != null) persist(state)
      applyLoaded(state, loadHoaDonPosStateForDuAn(next))
    },
    addTab: {
      reducer(state, action: PayloadAction<HoaDonTabSession>) {
        if (state.duAnId == null) {
          state.duAnId = currentDuAnIdFromProfile()
        }
        state.tabs.push(action.payload)
        state.activeTabId = action.payload.tabId
        if (action.payload.voucherType === 'traHang') {
          state.traHangCounter += 1
        } else if (action.payload.voucherType === 'datHang') {
          state.datHangCounter += 1
        } else if (action.payload.voucherType === 'yeuCauSuaChua') {
          state.yeuCauSuaChuaCounter += 1
        } else {
          state.counter += 1
        }
        persist(state)
      },
      prepare(
        session: Omit<HoaDonTabSession, 'tabId' | 'createdAt' | 'voucherType'> & {
          tabId?: string
          voucherType?: VoucherType
        },
      ) {
        return {
          payload: {
            ...session,
            voucherType: session.voucherType ?? 'hoaDon',
            tabId: session.tabId ?? `tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            createdAt: Date.now(),
          } as HoaDonTabSession,
        }
      },
    },
    closeTab(state, action: PayloadAction<{ tabId: string; force?: boolean }>) {
      const { tabId } = action.payload
      const idx = state.tabs.findIndex((t) => t.tabId === tabId)
      if (idx === -1) return
      state.tabs.splice(idx, 1)
      if (state.activeTabId === tabId) {
        const next = state.tabs[idx] ?? state.tabs[idx - 1] ?? state.tabs[state.tabs.length - 1] ?? null
        state.activeTabId = next ? next.tabId : null
      }
      // Reset counter khi tat ca tab da dong → tab tiep theo bat dau lai tu "Hoa don so 1"
      if (state.tabs.length === 0) {
        state.counter = 0
        state.traHangCounter = 0
        state.datHangCounter = 0
        state.yeuCauSuaChuaCounter = 0
      }
      persist(state)
    },
    closeOtherTabs(state, action: PayloadAction<{ keepTabId: string }>) {
      if (!state.tabs.some((t) => t.tabId === action.payload.keepTabId)) return
      state.tabs = state.tabs.filter((t) => t.tabId === action.payload.keepTabId)
      state.activeTabId = action.payload.keepTabId
      if (state.tabs.length === 0) {
        state.counter = 0
        state.traHangCounter = 0
        state.datHangCounter = 0
        state.yeuCauSuaChuaCounter = 0
      }
      persist(state)
    },
    closeTabsToRight(state, action: PayloadAction<{ anchorTabId: string }>) {
      const idx = state.tabs.findIndex((t) => t.tabId === action.payload.anchorTabId)
      if (idx === -1) return
      state.tabs = state.tabs.slice(0, idx + 1)
      if (!state.tabs.some((t) => t.tabId === state.activeTabId)) {
        state.activeTabId = action.payload.anchorTabId
      }
      persist(state)
    },
    setActiveTab(state, action: PayloadAction<string>) {
      if (state.tabs.some((t) => t.tabId === action.payload)) {
        state.activeTabId = action.payload
        persist(state)
      }
    },
    updateTabTitle(state, action: PayloadAction<{ tabId: string; title: string }>) {
      const tab = state.tabs.find((t) => t.tabId === action.payload.tabId)
      if (tab) {
        tab.title = action.payload.title
        persist(state)
      }
    },
    updateTabFormData(
      state,
      action: PayloadAction<{ tabId: string; formData: HoaDonTabFormData }>,
    ) {
      const tab = state.tabs.find((t) => t.tabId === action.payload.tabId)
      if (tab) {
        tab.formData = action.payload.formData
        tab.dirty = true
        persist(state)
      }
    },
    markTabClean(state, action: PayloadAction<{ tabId: string; savedHoaDonId?: string }>) {
      const tab = state.tabs.find((t) => t.tabId === action.payload.tabId)
      if (tab) {
        tab.dirty = false
        if (action.payload.savedHoaDonId) tab.savedHoaDonId = action.payload.savedHoaDonId
        persist(state)
      }
    },
    /**
     * Đổi tab tạo mới Hóa đơn ↔ Đặt hàng, giữ dữ liệu đang nhập.
     * Xóa mã phiếu: mã HĐ/ĐH khác dãy — form ĐH tự lấy mã mới, HĐ do BE sinh.
     */
    switchTabVoucherType(
      state,
      action: PayloadAction<{ tabId: string; voucherType: 'hoaDon' | 'datHang'; title: string }>,
    ) {
      const tab = state.tabs.find((t) => t.tabId === action.payload.tabId)
      if (!tab || tab.type !== 'new' || tab.voucherType === action.payload.voucherType) return
      tab.voucherType = action.payload.voucherType
      tab.title = action.payload.title
      if (tab.formData) tab.formData.maHoaDon = ''
      persist(state)
    },
    /** Đóng mọi tab tạo mới chưa nhập gì (vd. khi đóng modal POS ở list). */
    closeEmptyNewTabs(state) {
      const next = withoutEmptyTabs(state)
      if (next.tabs.length === state.tabs.length) return
      state.tabs = next.tabs
      state.activeTabId = next.activeTabId
      state.counter = next.counter
      state.traHangCounter = next.traHangCounter
      state.datHangCounter = next.datHangCounter
      state.yeuCauSuaChuaCounter = next.yeuCauSuaChuaCounter
      persist(state)
    },
    clearAllTabs(state) {
      state.tabs = []
      state.activeTabId = null
      state.counter = 0
      state.traHangCounter = 0
      state.datHangCounter = 0
      state.yeuCauSuaChuaCounter = 0
      persist(state)
    },
  },
})

export const {
  hydrateHoaDonPosForDuAn,
  addTab,
  closeTab,
  closeOtherTabs,
  closeTabsToRight,
  setActiveTab,
  updateTabTitle,
  updateTabFormData,
  markTabClean,
  closeEmptyNewTabs,
  switchTabVoucherType,
  clearAllTabs,
} = hoaDonPosDucks.actions

export default hoaDonPosDucks.reducer
